<template>
  <v-col cols="9" class="pl-0 ml-0">
    <div class="scrollable-form-content">
      <v-card-text>
        <v-form ref="form" v-model="isValid" lazy-validation>
          <v-checkbox v-model="localForm.enabled" label="Enabled" />
          <v-row class="overflow-y-auto">
            <v-col md="12" class="d-flex align-center justify-space-between pb-0">
              <v-autocomplete
                v-model="localForm.locations"
                :items="location"
                placeholder="Enter locations for your operator"
                dense
                outlined
                :append-icon="$vuetify.icons.values.chevronDown"
                item-text="name"
                item-value="uuid"
                chips
                small-chips
                persistent-hint
                deletable-chips
                multiple
                class="chips-truncate-autocomplete"
                @change="emitChange">
                <template #label>
                  <span class="pl-1 pr-1">Location list</span>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-start justify-space-between">
                      <span class="font-weight-regular">{{ data.item.name }}</span>
                      <span class="font-weight-light">{{ data.item['total_number'] || 0 }} devices and locations</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ index }">
                  <span v-if="index === 0" class="d-inline-flex align-center chips-selection-wrapper">
                    <v-chip
                      v-for="loc in visibleLocations"
                      :key="loc"
                      small
                      close
                      class="mr-1"
                      @click:close="removeLocation(loc)"
                    >
                      {{ getLocationName(loc) }}
                    </v-chip>
                    <span v-if="localForm.locations.length > visibleLocations.length" class="grey--text caption ml-1">
                      +{{ localForm.locations.length - visibleLocations.length }}
                    </span>
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-start justify-space-between px-4">
                      <span class="font-weight-light caption">No locations found</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-text-field dense outlined v-model="localForm.visionId" label="Vision ID" @input="emitChange" />
          <v-text-field dense outlined v-model="localForm.externalId" label="External ID" @input="emitChange" />
          <v-row class="overflow-y-auto mb-12">
            <v-col md="12" class="d-flex align-center justify-space-between">
              <v-autocomplete
                v-model="localForm.tag_uuids"
                :items="tags"
                placeholder="Enter tags for your operator"
                dense
                outlined
                height="40px"
                :append-icon="$vuetify.icons.values.chevronDown"
                @update:search-input="onTagChange"
                :counter="20"
                item-text="name"
                item-value="uuid"
                @keydown.enter="onAddTag"
                :search-input.sync="tagToAdd"
                @input="tagToAdd = null"
                @change="emitChange"
                chips
                small-chips
                hint="asd"
                persistent-hint
                maxlength="20"
                deletable-chips
                multiple
                class="chips-truncate-autocomplete">
                <template v-slot:message>
                  <div class="mt-2 ml-n3 mb-10">
                    <span>{{ $t('assets.locations.locationsInfoEdit.tagsSubtitle') }}</span>
                  </div>
                </template>
                <template v-slot:label>
                  <span class="pl-1 pr-1">{{ $t('assets.locations.locationsInfoEdit.tags') }}</span>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-start justify-space-between">
                      <span class="font-weight-regular">{{ data.item.name }}</span>
                      <span class="font-weight-light">{{ data.item['total_number'] || 0 }} devices and locations</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ index }">
                  <span v-if="index === 0" class="d-inline-flex align-center chips-selection-wrapper">
                    <v-chip
                      v-for="tagUuid in visibleTags"
                      :key="tagUuid"
                      small
                      close
                      class="mr-1"
                      @click:close="removeTag(tagUuid)"
                    >
                      {{ getTagName(tagUuid) }}
                    </v-chip>
                    <span v-if="localForm.tag_uuids.length > visibleTags.length" class="grey--text caption ml-1">
                      +{{ localForm.tag_uuids.length - visibleTags.length }}
                    </span>
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-start justify-space-between px-4 ">
                      <span class="font-weight-regular caption mb-8">{{ tagToAdd }}</span>
                      <span class="font-weight-light caption">New</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-autocomplete
            v-model="localForm.operator_group_uuids"
            :items="getOperatorGroups"
            placeholder="Select operator groups"
            dense
            outlined
            :append-icon="$vuetify.icons.values.chevronDown"
            item-text="name"
            item-value="uuid"
            chips
            small-chips
            deletable-chips
            multiple
            class="chips-truncate-autocomplete"
            :rules="[rules.requiredArray]"
            @change="emitChange"
          >
            <template #label>
              <span class="pl-1 pr-1">Operator Groups</span>
              <span class="red--text">*</span>
            </template>
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-start justify-space-between">
                  <span class="font-weight-regular">{{ data.item.name }}</span>
                  <span class="font-weight-light">{{ data.item.description || '' }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ index }">
              <span v-if="index === 0" class="d-inline-flex align-center chips-selection-wrapper">
                <v-chip
                  v-for="groupUuid in visibleOperatorGroups"
                  :key="groupUuid"
                  small
                  close
                  class="mr-1"
                  @click:close="removeOperatorGroup(groupUuid)"
                >
                  {{ getOperatorGroupName(groupUuid) }}
                </v-chip>
                <span v-if="localForm.operator_group_uuids.length > visibleOperatorGroups.length" class="grey--text caption ml-1">
                  +{{ localForm.operator_group_uuids.length - visibleOperatorGroups.length }}
                </span>
              </span>
            </template>
            <template v-slot:no-data>
              <v-list-item-content>
                <v-list-item-title class="d-flex align-start justify-space-between px-4">
                  <span class="font-weight-light caption">No groups found</span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <small>* Required field</small>
          <h3 class="mt-4">Custom fields</h3>
          <br />
          <v-row>
            <v-col cols="12" v-for="field in operatorCustomFields" :key="field.index">
              <v-text-field
                dense
                outlined
                :label="field.alias || `Custom field ${field.index}`"
                :required="field.required"
                :rules="field.required ? [rules.required] : []"
                :error-messages="fieldErrors[`operator_custom_${field.index}`] || []"
                v-model="localForm[`customField${field.index}`]"
                @input="clearFieldError(`operator_custom_${field.index}`); emitChange()"
              >
                <template v-slot:label>
                  {{ field.alias || `Custom field ${field.index}` }}
                  <span v-if="field.required" class="red--text">*</span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </div>
  </v-col>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { mdiMenuDown } from '@mdi/js';
export default {
  name: 'AdvancedOperatorInfo',
  props: { form: { type: Object, required: true } },
  data() {
    return {
      icons: {
        mdiMenuDown: mdiMenuDown
      },
      isValid: false,
      fieldErrors: {},
      tagToAdd: "",
      hasAutoSelectedGroups: false, // Flag to track if we've auto-selected
      operatorGroups: ['All permissions', 'Low permissions', 'Medium permissions'],
      rules: {
        requiredArray: v => (Array.isArray(v) && v.length > 0) || 'At least one group is required',
        required: v => !!v || 'This field is required',
      },
      localForm: {
        enabled: true,
        locations: [],
        visionId: '',
        externalId: '',
        tag_uuids: [],
        operator_group_uuids: [],
      },
    };
  },
  mounted() {
    this.setLocations();
    this.getTags("operator");
    this.setOperatorGroups().then(() => {
      this.autoSelectAutomaticGroups();
    }).catch(err => {
      console.error('Failed to fetch operator groups:', err);
    });
    this.fetchSpaceCustomFields(); // Fetch space custom fields
    
    // Initialize form with parent data
    this.syncFormData();
    
    // Initialize custom fields dynamically after space config is loaded
      this.initializeCustomFields();
  },
  watch: {
    form: {
      handler(newVal) {
        if (newVal) {
          this.syncFormData();
        }
      },
      immediate: true,
      deep: true
    },
    operatorCustomFields: {
      handler(newFields) {
        if (newFields && newFields.length > 0) {
          this.$nextTick(() => {
            this.initializeCustomFields();
          });
        }
      },
      immediate: true
    },
    getOperatorGroups: {
      handler(groups) {
        if (!groups || groups.length === 0) return;
        this.autoSelectAutomaticGroups();
      },
      immediate: false
    }
  },
  methods: {
    ...mapActions({
      setLocations: "locations/getLocationForLists",
      getTags: "tags/getTags", 
      addTag: "tags/addTag", 
      setOperatorGroups: "operators/fetchOperatorGroups",
      fetchSpaceCustomFields: "operators/fetchSpaceCustom_s"
    }),
    autoSelectAutomaticGroups() {
      if (this.hasAutoSelectedGroups) return;
      if (this.localForm.operator_group_uuids && this.localForm.operator_group_uuids.length > 0) return;
      
      const groups = this.getOperatorGroups;
      if (!groups || groups.length === 0) return;
      
      const automaticGroups = groups
        .filter(g => g.automatic_operator_group === true)
        .map(g => g.uuid);
      
      if (automaticGroups.length > 0) {
        this.localForm.operator_group_uuids = [...automaticGroups];
        this.hasAutoSelectedGroups = true;
        this.$nextTick(() => {
          this.emitChange();
        });
      }
    },
    syncFormData() {
      if (this.form) {
        Object.keys(this.localForm).forEach(key => {
          if (key === 'operator_group_uuids' && this.hasAutoSelectedGroups) {
            if (this.form[key] && this.form[key].length > 0) {
              this.localForm[key] = this.form[key];
            }
            return;
          }
          if (this.form[key] !== undefined) {
            this.localForm[key] = this.form[key];
          }
        });
        for (let i = 1; i <= 8; i++) {
          const key = `customField${i}`;
          if (this.form[key] !== undefined) {
            this.$set(this.localForm, key, this.form[key]);
          }
        }
      }
    },
    initializeCustomFields() {
      // Dynamically add custom field properties to localForm
      this.operatorCustomFields.forEach(field => {
        const key = `customField${field.index}`;
        if (!this.localForm[key]) {
          this.$set(this.localForm, key, '');
        }
      });
    },
    validate() { return this.$refs.form.validate(); },
    setFieldError(fieldKey, message) {
      this.$set(this.fieldErrors, fieldKey, [message]);
    },
    clearFieldError(fieldKey) {
      if (this.fieldErrors[fieldKey]) {
        this.$set(this.fieldErrors, fieldKey, []);
      }
    },
    clearFieldErrors() {
      this.fieldErrors = {};
    },
    onAddTag() {
      this.addTag(this.tagToAdd).then(() => {
        
      }).catch((error) => {
        console.log(error);
      })
    },
    onTagChange(tag) {
      this.tagToAdd = tag;
    },
    emitChange() {
      this.$emit('update:form', { ...this.localForm });
    },
    getLocationName(uuid) {
      const loc = (this.location || []).find(l => l.uuid === uuid);
      return loc ? loc.name : uuid;
    },
    getTagName(uuid) {
      const tag = (this.tags || []).find(t => t.uuid === uuid);
      return tag ? tag.name : uuid;
    },
    getOperatorGroupName(uuid) {
      const group = (this.getOperatorGroups || []).find(g => g.uuid === uuid);
      return group ? group.name : uuid;
    },
    removeLocation(uuid) {
      const index = this.localForm.locations.indexOf(uuid);
      if (index >= 0) {
        this.localForm.locations.splice(index, 1);
        this.emitChange();
      }
    },
    removeTag(uuid) {
      const index = this.localForm.tag_uuids.indexOf(uuid);
      if (index >= 0) {
        this.localForm.tag_uuids.splice(index, 1);
        this.emitChange();
      }
    },
    removeOperatorGroup(uuid) {
      const index = this.localForm.operator_group_uuids.indexOf(uuid);
      if (index >= 0) {
        this.localForm.operator_group_uuids.splice(index, 1);
        this.emitChange();
      }
    },
  },
  computed:{
    ...mapGetters({
      tags: "tags/tags",
      location: "locations/getLocationList",
      getOperatorGroups: "operators/getGroups",
      spaceCustomFields: "operators/getSpaceConfig",
      operatorCustomFieldsFromStore: "operators/getOperatorCustom_s"
    }),
    operatorCustomFields() {
      // Safety check: return empty array if store data is not yet loaded
      if (!this.operatorCustomFieldsFromStore || !Array.isArray(this.operatorCustomFieldsFromStore)) {
        return [];
      }
      return this.operatorCustomFieldsFromStore.map(field => ({
        ...field,
        value: this.localForm[`customField${field.index}`] || ''
      }));
    },
    visibleLocations() {
      const maxVisible = 2;
      return (this.localForm.locations || []).slice(0, maxVisible);
    },
    visibleTags() {
      const maxVisible = 2;
      return (this.localForm.tag_uuids || []).slice(0, maxVisible);
    },
    visibleOperatorGroups() {
      const maxVisible = 2;
      return (this.localForm.operator_group_uuids || []).slice(0, maxVisible);
    }
  }
};
</script>

<style>
.scrollable-form-content {
  max-height: 70vh;
  min-height: 200px;
  overflow-y: auto;
  padding-bottom: 24px;
}

.scrollable-card {
  max-height: calc(100vh - 100px); /* Set a maximum height */
  overflow-y: auto;  /* Enable vertical scrolling */
}

.scrollable-card.stepper-scroll-wrapper {
  max-height: none;
  min-height: 0;
  overflow-y: auto;
  margin-bottom: 32px; /* Add margin to bottom for spacing */
}

.chips-truncate-autocomplete .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}

.chips-selection-wrapper {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
}

.chips-selection-wrapper .v-chip {
  flex-shrink: 0;
}
</style>
